// =====================================================
// ALTA DE PRODUCTOR AGROPECUARIO
// Consulta a ARCA - Constancia de Inscripción
// src/pages/Agro/AltaProductor/index.js
//
// BDSJEA-4: la pantalla ya no busca al productor en Ewan. El alta arranca
// siempre con una consulta al padrón de ARCA por CUIT, y los datos del
// productor se prellenan con lo que devuelve la constancia de inscripción.
// =====================================================

import React, { useState, useEffect } from "react"
import { useHistory } from "react-router-dom"
import {
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
  Input,
  Button,
  Spinner,
  Alert,
  Badge,
  Table,
} from "reactstrap"

import Layout from "components/Layout"
import {
  getTiposProductor,
  getEntidades,
  saveProductor,
  validarCuit,
} from "api/agro"
import toastHandler from "toastHandler"
import { getErrorMessage } from "helpers/error_helpers"
import { idProductorExistente } from "./productorExistente"
import BotonImportacionMasiva from "pages/Agro/BusquedaProductor/BotonImportacionMasiva"

const AltaProductor = () => {
  const history = useHistory()
  const permissionBy = "agro.alta"

  const setError = toastHandler(state => state.setError)
  const setSuccess = toastHandler(state => state.setSuccess)
  const setWarnings = toastHandler(state => state.setWarnings)

  // Estados
  const [consultando, setConsultando] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const [cuit, setCuit] = useState("")
  const [constancia, setConstancia] = useState(null)
  // BDSJEA-168: tiposProductor sigue cargandose porque otras vistas lo usan para
  // mostrar la descripcion; el estado de seleccion se elimino junto con el combo.
  const [tiposProductor, setTiposProductor] = useState([])
  const [entidades, setEntidades] = useState([])
  const [entidadSeleccionada, setEntidadSeleccionada] = useState("")
  const [oficialComercial, setOficialComercial] = useState("")
  const [telefono, setTelefono] = useState("")
  const [email, setEmail] = useState("")
  const [errorConsulta, setErrorConsulta] = useState(null)
  const [errorGuardado, setErrorGuardado] = useState(null)

  // Carga manual: se habilita cuando ARCA no respondió (fuente LOCAL).
  // El alta no debe quedar bloqueada por una caída del padrón.
  const [manual, setManual] = useState({
    razonSocial: "",
    tipoPersona: "",
    condicionIva: "",
    direccion: "",
    localidad: "",
    provincia: "",
    codigoPostal: "",
    actividadPrincipalCodigo: "",
    actividadPrincipalDescripcion: "",
  })

  const esCargaManual =
    constancia?.requiereCargaManual || constancia?.fuente !== "ARCA"

  const setManualField = (campo, valor) =>
    setManual(prev => ({ ...prev, [campo]: valor }))

  useEffect(() => {
    getTiposProductor()
      .then(data => setTiposProductor(data))
      .catch(err => console.error("Error cargando tipos de productor:", err))
    getEntidades()
      .then(data => setEntidades(data))
      .catch(err => console.error("Error cargando entidades:", err))
  }, [])

  const formatearCuit = valor => {
    if (!valor || valor.length !== 11) return valor
    return `${valor.slice(0, 2)}-${valor.slice(2, 10)}-${valor.slice(10)}`
  }

  // ---------------------------------------------------
  // Consulta a ARCA
  // ---------------------------------------------------
  const handleConsultar = async () => {
    if (!cuit || cuit.length !== 11) {
      setErrorConsulta("El CUIT debe tener 11 dígitos")
      return
    }

    setConsultando(true)
    setErrorConsulta(null)
    setConstancia(null)
    setTelefono("")
    setEmail("")

    try {
      const datos = await validarCuit(cuit)

      if (!datos) {
        setErrorConsulta("ARCA no devolvió información para el CUIT ingresado")
        return
      }

      if (!datos.valido) {
        setErrorConsulta(datos.mensaje || "El CUIT no es válido en ARCA")
        return
      }

      setConstancia(datos)
      setManual({
        razonSocial: datos.razonSocial || "",
        tipoPersona:
          datos.tipoPersona ||
          (cuit.startsWith("30") ||
          cuit.startsWith("33") ||
          cuit.startsWith("34")
            ? "JURIDICA"
            : "FISICA"),
        condicionIva: datos.condicionIva || "",
        direccion: datos.direccion || "",
        localidad: datos.localidad || "",
        provincia: datos.provincia || "",
        codigoPostal: datos.codigoPostal || "",
        actividadPrincipalCodigo: datos.actividadPrincipalCodigo || "",
        actividadPrincipalDescripcion:
          datos.actividadPrincipalDescripcion || "",
      })
    } catch (err) {
      // BDSJEA-312: mensaje de la API o descriptivo por tipo (nunca el crudo de axios).
      const mensaje = getErrorMessage(err, {
        fallback: "Error al consultar el padrón de ARCA",
      })
      if (mensaje) setErrorConsulta(mensaje)
    } finally {
      setConsultando(false)
    }
  }

  /**
   * Habilita el alta manual cuando la consulta a ARCA falló por completo
   * (500, timeout, certificado vencido). El alta del productor no debe quedar
   * bloqueada por una caída del padrón.
   */
  const habilitarCargaManual = () => {
    const cuitLimpio = cuit.trim()
    setConstancia({
      cuit: cuitLimpio,
      cuitFormateado: formatearCuit(cuitLimpio),
      valido: true,
      fuente: "LOCAL",
      requiereCargaManual: true,
      estadoCuit: "PENDIENTE_VERIFICACION",
      actividades: [],
      impuestos: [],
      caracterizaciones: [],
    })
    setManual(prev => ({
      ...prev,
      tipoPersona:
        ["30", "33", "34"].indexOf(cuitLimpio.slice(0, 2)) >= 0
          ? "JURIDICA"
          : "FISICA",
    }))
    setErrorConsulta(null)
  }

  const handleLimpiar = () => {
    setCuit("")
    setConstancia(null)
    setTelefono("")
    setEmail("")
    setErrorConsulta(null)
    setErrorGuardado(null)
  }

  // ---------------------------------------------------
  // Alta del productor
  // ---------------------------------------------------
  const handleGuardar = async () => {
    if (!constancia) {
      setErrorGuardado("Primero debe consultar un CUIT en ARCA")
      return
    }

    if (esCargaManual && !manual.razonSocial.trim()) {
      setErrorGuardado(
        "ARCA no devolvió los datos: complete al menos la razón social / denominación",
      )
      return
    }

    setGuardando(true)
    setErrorGuardado(null)

    try {
      const domicilioManual = [
        manual.direccion,
        manual.localidad,
        manual.provincia,
      ]
        .filter(Boolean)
        .join(", ")

      const productorData = {
        cuit: constancia.cuit,
        razonSocial: esCargaManual
          ? manual.razonSocial
          : constancia.razonSocial,
        tipoPersona: esCargaManual
          ? manual.tipoPersona
          : constancia.tipoPersona,
        // BDSJEA-168: lo resuelve el backend al cargarse los establecimientos.
        estadoCuit: constancia.estadoCuit,
        condicionIva: esCargaManual
          ? manual.condicionIva
          : constancia.condicionIva,
        domicilioFiscal: esCargaManual
          ? domicilioManual
          : constancia.domicilioFiscal,
        direccion: esCargaManual ? manual.direccion : constancia.direccion,
        localidad: esCargaManual ? manual.localidad : constancia.localidad,
        provincia: esCargaManual ? manual.provincia : constancia.provincia,
        idProvinciaArca: constancia.idProvinciaArca,
        codigoPostal: esCargaManual
          ? manual.codigoPostal
          : constancia.codigoPostal,
        esMonotributista: constancia.esMonotributista,
        categoriaMonotributo: constancia.categoriaMonotributo,
        actividadPrincipalCodigo: esCargaManual
          ? manual.actividadPrincipalCodigo
          : constancia.actividadPrincipalCodigo,
        actividadPrincipalDescripcion: esCargaManual
          ? manual.actividadPrincipalDescripcion
          : constancia.actividadPrincipalDescripcion,
        telefono: telefono || null,
        email: email || null,
        idEntidad: entidadSeleccionada
          ? parseInt(entidadSeleccionada, 10)
          : null,
        oficialComercial: oficialComercial || null,
      }

      const nuevoProductor = await saveProductor(productorData)

      setSuccess(
        `Productor "${productorData.razonSocial}" creado correctamente`,
      )
      history.push(`/agro/productor/${nuevoProductor.id}`)
    } catch (err) {
      // BDSJEA-406: el CUIT ya existe -> el backend responde 409 con codigo
      // PRODUCTOR_CUIT_EXISTENTE e idProductor del existente: se lleva a su
      // pantalla en lugar de dejar un error en el formulario.
      const idExistente = idProductorExistente(err)
      if (idExistente != null) {
        setWarnings(
          getErrorMessage(err, {
            fallback: "Ya existe un productor con ese CUIT.",
          }),
        )
        history.push(`/agro/productor/${idExistente}`)
        return
      }
      const mensajeError = getErrorMessage(err, {
        fallback: "Error al guardar el productor",
      })
      if (mensajeError) {
        setErrorGuardado(mensajeError)
        setError({ message: mensajeError })
      }
    } finally {
      setGuardando(false)
    }
  }

  // ---------------------------------------------------
  // Helpers de render
  // ---------------------------------------------------
  const Dato = ({ label, valor }) =>
    valor ? (
      <div className="mb-2">
        <strong>{label}:</strong> {valor}
      </div>
    ) : null

  const badgeEstado = estado => {
    if (!estado) return null
    const color =
      estado === "ACTIVO"
        ? "success"
        : estado === "PENDIENTE_VERIFICACION"
          ? "warning"
          : "danger"
    return <Badge color={color}>{estado}</Badge>
  }

  const breadcrumbItems = [
    { title: "EWAN", link: "/" },
    { title: "Agro", link: "/agro" },
    { title: "Alta Productor", link: "#" },
  ]

  return (
    <Layout
      title="Alta de Productor Agropecuario"
      breadcrumbItems={breadcrumbItems}
      permissionBy={permissionBy}
      components={[
        {
          component: (
            <Row>
              <Col lg={10} className="mx-auto">
                {/* Consulta a ARCA */}
                <Card className="mb-4">
                  <CardBody>
                    <h5 className="card-title mb-4">
                      <i className="mdi mdi-file-document-outline me-2" />
                      Consulta de Constancia de Inscripción — ARCA
                    </h5>

                    <FormGroup className="mb-0">
                      <Label>CUIT del productor</Label>
                      <Row>
                        <Col md={5}>
                          <Input
                            type="text"
                            value={cuit}
                            onChange={e =>
                              setCuit(e.target.value.replace(/\D/g, ""))
                            }
                            placeholder="Ingrese CUIT sin guiones (11 dígitos)"
                            maxLength={11}
                            disabled={consultando}
                            onKeyDown={e => {
                              if (e.key === "Enter" && cuit.length === 11) {
                                handleConsultar()
                              }
                            }}
                          />
                        </Col>
                        <Col md={7} className="d-flex align-items-center">
                          <Button
                            color="primary"
                            onClick={handleConsultar}
                            disabled={consultando || cuit.length !== 11}
                            className="me-2"
                          >
                            {consultando ? (
                              <>
                                <Spinner size="sm" className="me-2" />
                                Consultando ARCA...
                              </>
                            ) : (
                              <>
                                <i className="mdi mdi-cloud-search-outline me-2" />
                                Consultar en ARCA
                              </>
                            )}
                          </Button>
                          <Button
                            color="secondary"
                            outline
                            onClick={handleLimpiar}
                            className="me-2"
                          >
                            <i className="mdi mdi-eraser me-1" />
                            Limpiar
                          </Button>
                          <BotonImportacionMasiva />
                        </Col>
                      </Row>
                    </FormGroup>

                    {errorConsulta && (
                      <Alert color="warning" className="mt-3 mb-0">
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                          <span>
                            <i className="mdi mdi-alert-circle me-2" />
                            {errorConsulta}
                          </span>
                          {cuit.length === 11 && (
                            <Button
                              color="warning"
                              outline
                              size="sm"
                              onClick={habilitarCargaManual}
                            >
                              <i className="mdi mdi-pencil me-1" />
                              Cargar datos manualmente
                            </Button>
                          )}
                        </div>
                      </Alert>
                    )}
                  </CardBody>
                </Card>

                {/* Datos de la constancia */}
                {constancia && (
                  <>
                    {constancia.advertencia && constancia.mensaje && (
                      <Alert color="warning">
                        <i className="mdi mdi-alert me-2" />
                        {constancia.mensaje}
                      </Alert>
                    )}

                    {constancia.requiereCargaManual && (
                      <Alert color="info">
                        <i className="mdi mdi-information me-2" />
                        No se pudo obtener la constancia desde ARCA. El CUIT es
                        válido, pero los datos del productor deberán completarse
                        manualmente luego del alta.
                        {constancia.urlConstanciaArca && (
                          <>
                            {" "}
                            <a
                              href={constancia.urlConstanciaArca}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Consultar constancia en el sitio de ARCA
                            </a>
                          </>
                        )}
                      </Alert>
                    )}

                    {constancia.caracterizaciones?.length > 0 && (
                      <Alert color="danger">
                        <i className="mdi mdi-alert-octagon me-2" />
                        <strong>Caracterizaciones informadas por ARCA:</strong>
                        <ul className="mb-0 mt-2">
                          {constancia.caracterizaciones.map((c, i) => (
                            <li key={i}>{c}</li>
                          ))}
                        </ul>
                      </Alert>
                    )}

                    <Card className="mb-4">
                      <CardBody>
                        <div className="d-flex justify-content-between align-items-start mb-4">
                          <h5 className="card-title mb-0">
                            <i className="mdi mdi-account-check me-2" />
                            Datos del Productor según ARCA
                          </h5>
                          <div>
                            {constancia.fuente === "ARCA" ? (
                              <Badge color="success">
                                <i className="mdi mdi-check-decagram me-1" />
                                Padrón ARCA
                              </Badge>
                            ) : (
                              <Badge color="secondary">Validación local</Badge>
                            )}
                          </div>
                        </div>

                        <Row>
                          <Col md={6}>
                            {esCargaManual ? (
                              <div className="border border-warning rounded p-3 mb-3">
                                <h6 className="text-muted mb-3">
                                  <i className="mdi mdi-pencil me-1" />
                                  Carga manual de datos
                                </h6>
                                <FormGroup>
                                  <Label>
                                    Razón social / Denominación{" "}
                                    <span className="text-danger">*</span>
                                  </Label>
                                  <Input
                                    type="text"
                                    value={manual.razonSocial}
                                    onChange={e =>
                                      setManualField(
                                        "razonSocial",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="Apellido y nombre o razón social"
                                  />
                                </FormGroup>
                                <Row>
                                  <Col md={6}>
                                    <FormGroup>
                                      <Label>Tipo de persona</Label>
                                      <Input
                                        type="select"
                                        value={manual.tipoPersona}
                                        onChange={e =>
                                          setManualField(
                                            "tipoPersona",
                                            e.target.value,
                                          )
                                        }
                                      >
                                        <option value="FISICA">Física</option>
                                        <option value="JURIDICA">
                                          Jurídica
                                        </option>
                                      </Input>
                                    </FormGroup>
                                  </Col>
                                  <Col md={6}>
                                    <FormGroup>
                                      <Label>Condición IVA</Label>
                                      <Input
                                        type="text"
                                        value={manual.condicionIva}
                                        onChange={e =>
                                          setManualField(
                                            "condicionIva",
                                            e.target.value,
                                          )
                                        }
                                        placeholder="Ej: IVA RESPONSABLE INSCRIPTO"
                                      />
                                    </FormGroup>
                                  </Col>
                                </Row>
                                <FormGroup>
                                  <Label>Dirección</Label>
                                  <Input
                                    type="text"
                                    value={manual.direccion}
                                    onChange={e =>
                                      setManualField(
                                        "direccion",
                                        e.target.value,
                                      )
                                    }
                                  />
                                </FormGroup>
                                <Row>
                                  <Col md={5}>
                                    <FormGroup>
                                      <Label>Localidad</Label>
                                      <Input
                                        type="text"
                                        value={manual.localidad}
                                        onChange={e =>
                                          setManualField(
                                            "localidad",
                                            e.target.value,
                                          )
                                        }
                                      />
                                    </FormGroup>
                                  </Col>
                                  <Col md={4}>
                                    <FormGroup>
                                      <Label>Provincia</Label>
                                      <Input
                                        type="text"
                                        value={manual.provincia}
                                        onChange={e =>
                                          setManualField(
                                            "provincia",
                                            e.target.value,
                                          )
                                        }
                                      />
                                    </FormGroup>
                                  </Col>
                                  <Col md={3}>
                                    <FormGroup>
                                      <Label>Cód. postal</Label>
                                      <Input
                                        type="text"
                                        value={manual.codigoPostal}
                                        onChange={e =>
                                          setManualField(
                                            "codigoPostal",
                                            e.target.value,
                                          )
                                        }
                                      />
                                    </FormGroup>
                                  </Col>
                                </Row>
                                <Row>
                                  <Col md={4}>
                                    <FormGroup className="mb-0">
                                      <Label>Cód. actividad</Label>
                                      <Input
                                        type="text"
                                        value={manual.actividadPrincipalCodigo}
                                        onChange={e =>
                                          setManualField(
                                            "actividadPrincipalCodigo",
                                            e.target.value.replace(/\D/g, ""),
                                          )
                                        }
                                        placeholder="Ej: 011110"
                                        maxLength={6}
                                      />
                                    </FormGroup>
                                  </Col>
                                  <Col md={8}>
                                    <FormGroup className="mb-0">
                                      <Label>Actividad principal</Label>
                                      <Input
                                        type="text"
                                        value={
                                          manual.actividadPrincipalDescripcion
                                        }
                                        onChange={e =>
                                          setManualField(
                                            "actividadPrincipalDescripcion",
                                            e.target.value,
                                          )
                                        }
                                        placeholder="Ej: CULTIVO DE SOJA"
                                      />
                                    </FormGroup>
                                  </Col>
                                </Row>
                              </div>
                            ) : (
                              <>
                                <div className="border rounded p-3 bg-light mb-3">
                                  <h6 className="text-muted mb-3">
                                    Identificación
                                  </h6>

                                  <Dato
                                    label="CUIT"
                                    valor={
                                      constancia.cuitFormateado ||
                                      formatearCuit(constancia.cuit)
                                    }
                                  />
                                  <Dato
                                    label="Denominación"
                                    valor={constancia.razonSocial}
                                  />
                                  <div className="mb-2">
                                    <strong>Tipo de persona:</strong>{" "}
                                    <Badge
                                      color={
                                        constancia.esPJ === "S"
                                          ? "info"
                                          : "secondary"
                                      }
                                    >
                                      {constancia.esPJ === "S"
                                        ? "Jurídica"
                                        : "Física"}
                                    </Badge>
                                  </div>
                                  <div className="mb-2">
                                    <strong>Estado del CUIT:</strong>{" "}
                                    {badgeEstado(constancia.estadoCuit)}
                                  </div>
                                  <Dato
                                    label="Condición IVA"
                                    valor={constancia.condicionIva}
                                  />
                                  <Dato
                                    label="Categoría monotributo"
                                    valor={constancia.categoriaMonotributo}
                                  />
                                  <Dato
                                    label="Mes de cierre"
                                    valor={constancia.mesCierre}
                                  />
                                  <Dato
                                    label="Fecha contrato social"
                                    valor={constancia.fechaContratoSocial}
                                  />
                                  <Dato
                                    label="Sucesión indivisa"
                                    valor={
                                      constancia.esSucesion === "SI"
                                        ? "Sí"
                                        : null
                                    }
                                  />
                                </div>

                                <div className="border rounded p-3 bg-light mb-3">
                                  <h6 className="text-muted mb-3">
                                    Domicilio fiscal
                                  </h6>
                                  <Dato
                                    label="Dirección"
                                    valor={constancia.direccion}
                                  />
                                  <Dato
                                    label="Localidad"
                                    valor={constancia.localidad}
                                  />
                                  <Dato
                                    label="Provincia"
                                    valor={constancia.provincia}
                                  />
                                  <Dato
                                    label="Código postal"
                                    valor={constancia.codigoPostal}
                                  />
                                  {!constancia.direccion &&
                                    !constancia.localidad && (
                                      <span className="text-muted">
                                        ARCA no informó domicilio fiscal
                                      </span>
                                    )}
                                </div>
                              </>
                            )}
                          </Col>

                          <Col md={6}>
                            <div className="border rounded p-3 mb-3">
                              <h6 className="text-muted mb-3">
                                Actividad principal
                              </h6>
                              {constancia.actividadPrincipalDescripcion ? (
                                <>
                                  <div className="mb-2">
                                    <Badge color="primary" className="me-2">
                                      {constancia.actividadPrincipalCodigo}
                                    </Badge>
                                    {constancia.actividadPrincipalDescripcion}
                                  </div>
                                  <div>
                                    {constancia.esProductorAgro ? (
                                      <Badge color="success">
                                        <i className="mdi mdi-sprout me-1" />
                                        Actividad agropecuaria
                                      </Badge>
                                    ) : (
                                      <Badge color="warning">
                                        Sin actividad agropecuaria registrada
                                      </Badge>
                                    )}
                                  </div>
                                </>
                              ) : (
                                <span className="text-muted">
                                  ARCA no informó actividades
                                </span>
                              )}
                            </div>

                            <div className="border rounded p-3 mb-3">
                              <h6 className="text-muted mb-3">
                                Datos de contacto (opcional)
                              </h6>
                              <FormGroup>
                                <Label for="telefono">Teléfono</Label>
                                <Input
                                  type="text"
                                  id="telefono"
                                  value={telefono}
                                  onChange={e => setTelefono(e.target.value)}
                                  placeholder="No lo informa ARCA"
                                />
                              </FormGroup>
                              <FormGroup className="mb-0">
                                <Label for="email">Email</Label>
                                <Input
                                  type="email"
                                  id="email"
                                  value={email}
                                  onChange={e => setEmail(e.target.value)}
                                  placeholder="No lo informa ARCA"
                                />
                              </FormGroup>
                            </div>

                            {/* BDSJEA-168: el tipo de productor deja de
                                pedirse. Se deriva de la superficie total de los
                                establecimientos, contra los rangos de
                                dom_tipo_productor, y se recalcula solo cuando esa
                                superficie cambia. Pedirlo a mano lo dejaba a
                                criterio de quien cargaba y congelado para
                                siempre. */}
                            <div className="border rounded p-3 mb-3">
                              <h6 className="text-muted mb-3">
                                Clasificación agropecuaria
                              </h6>
                              <small className="text-muted">
                                <i className="mdi mdi-information-outline me-1" />
                                El tipo de productor (pequeño, mediano o grande)
                                se calcula automáticamente a partir de la
                                superficie total de los establecimientos que se
                                carguen, y se actualiza a medida que esa
                                superficie cambia.
                              </small>
                            </div>

                            <div className="border rounded p-3 mb-3">
                              <h6 className="text-muted mb-3">
                                Datos comerciales
                              </h6>
                              <FormGroup>
                                <Label for="entidad">Entidad principal</Label>
                                <Input
                                  type="select"
                                  id="entidad"
                                  value={entidadSeleccionada}
                                  onChange={e =>
                                    setEntidadSeleccionada(e.target.value)
                                  }
                                >
                                  <option value="">-- Seleccione --</option>
                                  {entidades.map(entidad => (
                                    <option key={entidad.id} value={entidad.id}>
                                      {entidad.descripcion}
                                    </option>
                                  ))}
                                </Input>
                              </FormGroup>
                              <FormGroup className="mb-0">
                                <Label for="oficialComercial">
                                  Oficial Comercial
                                </Label>
                                <Input
                                  type="text"
                                  id="oficialComercial"
                                  value={oficialComercial}
                                  onChange={e =>
                                    setOficialComercial(e.target.value)
                                  }
                                  placeholder="Nombre del oficial asignado"
                                />
                              </FormGroup>
                            </div>
                          </Col>
                        </Row>

                        {/* Actividades declaradas */}
                        {constancia.actividades?.length > 0 && (
                          <>
                            <h6 className="text-muted mb-3">
                              Actividades declaradas en ARCA (
                              {constancia.actividades.length})
                            </h6>
                            <Table responsive hover className="table-sm">
                              <thead className="table-light">
                                <tr>
                                  <th style={{ width: "110px" }}>Código</th>
                                  <th>Descripción</th>
                                  <th style={{ width: "90px" }}>Orden</th>
                                  <th style={{ width: "110px" }}>Período</th>
                                  <th style={{ width: "110px" }}>Principal</th>
                                </tr>
                              </thead>
                              <tbody>
                                {constancia.actividades.map((act, i) => (
                                  <tr
                                    key={`${act.codigo}-${i}`}
                                    className={
                                      act.principal ? "table-primary" : ""
                                    }
                                  >
                                    <td>{act.codigo}</td>
                                    <td>{act.descripcion}</td>
                                    <td>{act.orden ?? "-"}</td>
                                    <td>{act.periodo || "-"}</td>
                                    <td>
                                      {act.principal && (
                                        <Badge color="primary">
                                          <i className="mdi mdi-star me-1" />
                                          Principal
                                        </Badge>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </>
                        )}

                        {/* Impuestos */}
                        {constancia.impuestos?.length > 0 && (
                          <div className="mt-3">
                            <h6 className="text-muted mb-2">
                              Impuestos inscriptos
                            </h6>
                            {constancia.impuestos.map((imp, i) => (
                              <Badge
                                key={i}
                                color="light"
                                className="text-dark border me-2 mb-2"
                              >
                                {imp}
                              </Badge>
                            ))}
                          </div>
                        )}

                        {errorGuardado && (
                          <Alert color="danger" className="mt-3">
                            <i className="mdi mdi-alert-circle me-2" />
                            <strong>Error al guardar:</strong> {errorGuardado}
                          </Alert>
                        )}

                        <hr />

                        <div className="d-flex justify-content-between align-items-center">
                          <small className="text-muted">
                            {constancia.fechaConsulta && (
                              <>
                                <i className="mdi mdi-clock-outline me-1" />
                                Consulta a ARCA: {constancia.fechaConsulta}
                              </>
                            )}
                          </small>
                          <div className="d-flex gap-2">
                            <Button
                              color="secondary"
                              outline
                              onClick={() => history.goBack()}
                            >
                              <i className="mdi mdi-arrow-left me-1" />
                              Cancelar
                            </Button>
                            <Button
                              color="success"
                              onClick={handleGuardar}
                              disabled={
                                guardando ||
                                (esCargaManual && !manual.razonSocial.trim())
                              }
                            >
                              {guardando ? (
                                <>
                                  <Spinner size="sm" className="me-2" />
                                  Guardando...
                                </>
                              ) : (
                                <>
                                  <i className="mdi mdi-content-save me-2" />
                                  Crear Productor
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </>
                )}

                {/* Mensaje inicial */}
                {!constancia && !errorConsulta && (
                  <Alert
                    color="light"
                    className="text-center py-4"
                    fade={false}
                  >
                    <i
                      className="mdi mdi-file-search-outline d-block mb-2"
                      style={{ fontSize: "2rem" }}
                    />
                    Ingrese el CUIT del productor para consultar su constancia
                    de inscripción en ARCA y darlo de alta.
                  </Alert>
                )}
              </Col>
            </Row>
          ),
        },
      ]}
    />
  )
}

export default AltaProductor
